import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { getBestImageUrl } from '@/lib/utils';
import { formatDate, formatShortDateTime } from '@/lib/utils';
import { CartStatus, CartStatusLabels } from '@/features/cart/types';
import type { CartResponse } from '@/features/cart/types';
import DeleteCartModal from './DeleteCartModal';

interface CartCardProps {
  cart: CartResponse;
  onDelete: (cartId: string) => void;
  showDelete?: boolean;
}

const CartCard: React.FC<CartCardProps> = ({ cart, onDelete, showDelete = true }) => {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const business = cart.business;
  if (!business) return null;

  const backgroundImage = getBestImageUrl(business.card_image_id, business.media, '/placeholder-club.jpg');
  const formattedExpiration = formatDate(cart.expires_at);
  
  // Format purchase date from payment_intent_expires_at (uses local device time)
  const formattedPurchaseDate = cart.payment_intent_expires_at
    ? formatShortDateTime(cart.payment_intent_expires_at)
    : null;

  const getStatusBadgeClass = (status: CartStatus) => {
    switch (status) {
      case CartStatus.Active:
        return 'bg-green-500/90 text-white border-green-400';
      case CartStatus.PendingPayment:
        return 'bg-yellow-500/90 text-white border-yellow-400';
      case CartStatus.SuccessfulPayment:
      case CartStatus.Completed:
        return 'bg-blue-500/90 text-white border-blue-400';
      case CartStatus.FailedPayment:
        return 'bg-red-500/90 text-white border-red-400';
      case CartStatus.Abandoned:
        return 'bg-gray-600/90 text-white border-gray-400';
      case CartStatus.Refunded:
        return 'bg-orange-500/90 text-white border-orange-400';
      default:
        return 'bg-gray-600/90 text-white border-gray-400';
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or delete area
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[data-no-navigate]')) {
      return;
    }
    navigate(`/carts/${cart.id}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(cart.id);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting cart:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        whileHover={{ y: -4 }}
        onClick={handleCardClick}
        className={`relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer ${cart.is_guest_cart ? 'ring-1 ring-gray-500/40' : ''}`}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: cart.is_guest_cart ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {/* Background Image - Full Width */}
        <div className="relative h-48 overflow-hidden">
          <motion.img
            src={backgroundImage}
            alt={business.name}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.5 }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/placeholder-club.jpg';
            }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          
          {/* Status Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="absolute top-4 left-4 z-10 flex items-center gap-2"
          >
            <span className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 backdrop-blur-sm ${getStatusBadgeClass(cart.status)}`}>
              {CartStatusLabels[cart.status]}
            </span>
            {cart.is_guest_cart && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-700/80 backdrop-blur-sm border border-gray-500/60 shadow-lg z-30"
              >
                <svg 
                  className="w-5 h-5 text-gray-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                  />
                </svg>
                <span className="text-sm font-semibold text-gray-300">Guest Cart</span>
              </motion.div>
            )}
          </motion.div>

          {/* Delete Button - Top Right (only show if showDelete is true and cart is active) */}
          {showDelete && cart.status == CartStatus.Active && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                setIsDeleteModalOpen(true);
              }}
              data-no-navigate
              className="absolute top-4 right-4 p-2 bg-red-500/20 backdrop-blur-md text-white rounded-lg hover:bg-red-500/30 transition-all duration-200 border border-red-500/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </motion.button>
          )}
        </div>

        {/* Content - Glass Morphism Background */}
        <div 
          className="relative p-4 border-t border-white/20"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.08))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {/* Business Name and Location */}
          <div className="mb-1">
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-bold text-white mb-1"
            >
              {business.name}
            </motion.h3>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-2 text-gray-400 mb-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs">{business.location.city}, {business.location.country}</span>
            </motion.div>
          </div>

          {/* Expiration and Items */}
          <div className="flex items-center gap-4 text-sm text-gray-400">
            {/* Expiration Date */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Expires: {formattedExpiration}</span>
            </motion.div>

            {/* Items Count */}
            {cart.items && cart.items.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>{cart.items.length} {cart.items.length === 1 ? 'item' : 'items'}</span>
              </motion.div>
            )}
          </div>

          {/* Purchase Date */}
          {formattedPurchaseDate && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="flex items-center gap-2 text-sm text-gray-400 mt-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Purchase Date: {formattedPurchaseDate}</span>
            </motion.div>
          )}
        </div>

        {/* View Venue Button - Absolute Position Bottom Right */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/venues/${business.handle}`);
          }}
          data-no-navigate
          className="absolute bottom-4 right-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all duration-200 text-xs font-medium shadow-lg backdrop-blur-md"
        >
          View Venue
        </motion.button>
      </motion.div>

      <DeleteCartModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        businessName={business.name}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default CartCard;

